import { useState } from "react"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { errorToast, toast } from "@/v2-ui/toast"
import ViewSheet from "@/v2-backoffice-client/components/viewSheet"
import AppViewSheetBreadcrumb from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"
import FeatureCreateIdentity from "@/v2-backoffice-client/feature/create/FeatureCreateIdentity"
import FeatureConfigurationEditor from "@/v2-backoffice-client/feature/configuration/FeatureConfigurationEditor"
import { FEATURE_CODE_PATTERN } from "@/v2-backoffice-client/feature/feature.constants"
import {
  BASEMAP_CREATE_CONFIGURATION,
  getBasemapEditorModelPath,
} from "@/v2-backoffice-client/basemap/update/basemap.update.schema"
import { useBasemapCreateMutation } from "@/v2-backoffice-client/basemap/create/basemap.create.mutation"
import {
  BASEMAP_FEATURE_CODE_PREFIX,
  BASEMAP_QUERY_NAME,
} from "@/v2-backoffice-client/basemap/basemap.constants"
import { LAYER_COUNTS_QUERY_NAME } from "@/v2-backoffice-client/layer/counts/layer.counts.constants"
import { useAppViewSheetOpener } from "@/v2-backoffice-client/app/app.utils"
import type { FeatureCreateIdentityValue } from "@/v2-backoffice-client/feature/create/feature.create.types"

const EMPTY_IDENTITY: FeatureCreateIdentityValue = {
  featureCode: BASEMAP_FEATURE_CODE_PREFIX,
  name: "",
  description: "",
}

/**
 * The basemap create flow: identity, position and the same schema-assisted
 * configuration editor the sheet edits with.
 *
 * Nothing exists server-side until "Create basemap": the row lands
 * disabled, so a half-configured basemap can never reach the console's
 * chooser on the strength of a first save. The real sheet opens on success.
 */
function BasemapCreateSheet() {
  const opener = useAppViewSheetOpener()

  const [identity, setIdentity] = useState(EMPTY_IDENTITY)
  const [priority, setPriority] = useState("0")

  const [createBasemap, { loading: isCreating, error: createError }] =
    useBasemapCreateMutation({
      refetchQueries: [BASEMAP_QUERY_NAME, LAYER_COUNTS_QUERY_NAME],
    })
  errorToast(createError, { title: "Failed to create the basemap" })

  async function create(configuration: unknown) {
    const featureCode = identity.featureCode.trim()
    if (!FEATURE_CODE_PATTERN.test(featureCode)) {
      toast({
        title: "Fix the feature code first",
        description:
          "Letters, digits and underscores only, starting with a letter.",
        variant: "destructive",
      })
      return
    }
    if (!identity.name.trim()) {
      toast({
        title: "Name the basemap first",
        description: "The en-GB name is what customers see in the chooser.",
        variant: "destructive",
      })
      return
    }

    const result = await createBasemap({
      variables: {
        input: {
          FeatureCode: featureCode,
          Name: identity.name.trim(),
          Description: identity.description.trim() || undefined,
          Configuration: configuration,
          Priority: Number(priority) || 0,
        },
      },
    })

    const created = result.data?.createBasemap
    if (!created) return

    toast({
      title: "Basemap created.",
      description:
        "It starts disabled — invisible in the console until it goes live.",
    })
    opener.open("basemap", created.FeatureCode)
  }

  return (
    <>
      <ViewSheet.Header>
        <AppViewSheetBreadcrumb />
        <ViewSheet.Title>New basemap</ViewSheet.Title>
      </ViewSheet.Header>

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <FeatureCreateIdentity
          idPrefix="basemap-create"
          codePlaceholder={`${BASEMAP_FEATURE_CODE_PREFIX}…`}
          subject="basemap"
          identity={identity}
          onIdentityChange={setIdentity}
        >
          <div className="flex w-24 flex-col gap-1">
            <Label htmlFor="basemap-create-priority">Position</Label>
            <Input
              id="basemap-create-priority"
              type="number"
              value={priority}
              onChange={(event) => setPriority(event.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              Low first in the chooser.
            </p>
          </div>
        </FeatureCreateIdentity>

        {/* The editor owns the draft text — the create payload comes from
            its onSave, so nothing here has to mirror every keystroke. */}
        <FeatureConfigurationEditor
          className="min-h-[420px]"
          modelPath={`create/${getBasemapEditorModelPath()}`}
          value={BASEMAP_CREATE_CONFIGURATION}
          isSaving={isCreating}
          saveLabel="Create basemap"
          canSaveClean
          onSave={(configuration) => void create(configuration)}
        />
      </div>
    </>
  )
}

export default BasemapCreateSheet
